<template>
  <super-admin-layout>
    <v-container fluid class="contracts-page">
      <v-row>
        <v-col cols="12" md="3">
          <v-card flat>
            <v-card-actions>
              <v-text-field placeholder="Search by company or signer" prepend-icon="fa-search" v-model="search"
                type="text" clearable clear-icon="fa-times" @click:clear="resetSearch" @keyup="applySearch"
                class="search-box"></v-text-field>
            </v-card-actions>
          </v-card>
        </v-col>
        <v-col cols="12" md="3">
          <v-card flat class="v-card-bg-none">
            <v-card-actions>
              <v-select :items="statusOptions" :clearable="true" placeholder="Status" item-value="id" item-text="name"
                v-on:change="FilterByStatus($event)" class="search-box"></v-select>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
      <v-row>
        <v-col md="12">
          <v-skeleton-loader v-if="loading && isTabletOrDesktop" type="table" :types="{ 'table-heading': '' }"></v-skeleton-loader>
          <v-skeleton-loader v-if="loading && !isTabletOrDesktop" type="table" :types="{ 'table-heading': '', 'table-thead' : 'heading@1' ,'table-row':'text@1'}" class="slider-table"></v-skeleton-loader>
          <v-data-table :headers="headers" :options.sync="options" :server-items-length="serverItemsLength"
            v-show="!loading" :items="contracts" item-key="uuid" :item-class="rowClass">

            <template v-slot:item.status="{ item }">
              <v-chip small :color="item.status == 'signed' ? 'success' : 'grey lighten-1'" text-color="white">
                {{ item.status }}
              </v-chip>
            </template>

            <template v-slot:item.uuid="{ item }">
              <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                  <span v-on="on">
                    <v-btn :disabled="!item.has_pdf" :loading="downloadingUuid == item.uuid" outlined small
                      color="secondary" @click.prevent="downloadPdf(item)">
                      <v-icon small>fa-download</v-icon>
                    </v-btn>
                  </span>
                </template>
                <span>{{ item.has_pdf ? 'Download PDF' : 'PDF is created when payment is confirmed' }}</span>
              </v-tooltip>
              <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                  <span v-on="on">
                    <v-btn :disabled="item.status == 'replaced'" outlined small color="secondary" class="ml-1"
                      @click.prevent="showReplaceDialog(item)">
                      <v-icon small>fa-upload</v-icon>
                    </v-btn>
                  </span>
                </template>
                <span>{{ item.status == 'replaced' ? 'This contract has been superseded' : 'Replace PDF' }}</span>
              </v-tooltip>
            </template>
          </v-data-table>
        </v-col>
      </v-row>
    </v-container>
    <v-dialog v-model="replaceDialog" max-width="550px" scrollable persistent>
      <v-card>
        <v-card-title :class="this.$gf.headerColor()" style="color: white;">
          <span>Replace Contract PDF</span>
        </v-card-title>

        <v-container class="dialog-container">
          <v-row>
            <v-col cols="12">
              <p>
                You are replacing the signed contract document for
                <strong>{{ replaceItem.company_name }}</strong>
                <span v-if="replaceItem.signer_name"> (signed by {{ replaceItem.signer_name }})</span>.
              </p>
              <p class="error--text">
                This replaces the contract document the customer sees and downloads from their account.
                The current PDF will no longer be available to them. The replacement is recorded against
                the contract for audit purposes.
              </p>
              <v-file-input label="New contract PDF" accept=".pdf" v-model="replaceFile"
                :error-messages="messages.file" filled prepend-icon="fa-paperclip"></v-file-input>
              <v-checkbox v-model="replaceConfirmed"
                label="I understand this replaces the contract document the customer sees"></v-checkbox>
            </v-col>
          </v-row>
        </v-container>

        <v-card-actions class="btn-section pa-5">
          <v-spacer></v-spacer>
          <template v-if="replaceLoading">
            <v-btn color="primary" loading></v-btn>
          </template>
          <template v-else>
            <v-btn color="primary" @click="closeReplaceDialog">Cancel</v-btn>
            <v-btn color="primary" :disabled="!replaceFile || !replaceConfirmed" @click="processReplace">Replace</v-btn>
          </template>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </super-admin-layout>
</template>

<script>
import SuperAdminLayout from '@/layouts/SuperAdmin';
import contractAPI from '@/api/contract.js';

export default {
  name: 'SuperAdminContracts',
  components: {
    SuperAdminLayout,
  },
  data() {
    return {
      headers: [
        { text: 'Company', value: 'company_name', sortable: false },
        { text: 'Plan', value: 'plan_slug', sortable: false },
        { text: 'Signer', value: 'signer_name', sortable: false },
        { text: 'Signer Email', value: 'signer_email', sortable: false },
        { text: 'Signed', value: 'signed_at', sortable: false },
        { text: 'Version', value: 'template_version', sortable: false },
        { text: 'Status', value: 'status', sortable: false },
        { text: '', value: 'uuid', sortable: false, align: 'end' },
      ],
      contracts: [],
      search: '',
      searchParams: '',
      selectedStatus: '',
      statusOptions: [
        { id: 'signed', name: 'Signed' },
        { id: 'replaced', name: 'Replaced' },
      ],
      pagination: {},
      options: {},
      loading: false,
      timer: null,
      downloadingUuid: '',
      replaceDialog: false,
      replaceItem: {},
      replaceFile: null,
      replaceConfirmed: false,
      replaceLoading: false,
      messages: [],
      successReplaceMessage: 'Contract PDF replaced successfully.',
      errorMessage:
        'Oops, something went wrong! Please check information is correct.',
    };
  },
  computed: {
    serverItemsLength: function () {
      if (this.pagination && this.pagination.total) {
        return this.pagination.total;
      } else {
        return this.contracts.length;
      }
    },
    params: function () {
      return this.$gf.getQueryString(this.options, this.serverItemsLength);
    },
    isTabletOrDesktop() {
      const width = this.$vuetify.breakpoint.width;
      return width >= 820;
    },
  },
  watch: {
    options: {
      handler() {
        this.getContracts();
      },
      deep: true,
    },
  },
  methods: {
    getContracts() {
      this.loading = true;
      contractAPI
        .getContracts(this.params, this.searchParams, this.selectedStatus)
        .then((resp) => {
          this.contracts = resp.data.data;
          this.pagination = resp.data.meta.pagination;
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
          this.$snotify.error(this.errorMessage);
        });
    },
    applySearch() {
      clearTimeout(this.timer);
      this.timer = setTimeout(() => {
        this.options.page = 1;
        if (this.search != null && this.search != '') {
          this.searchParams = '&search=' + encodeURIComponent(this.search);
        } else {
          this.searchParams = '';
        }
        this.getContracts();
      }, 1000);
    },
    resetSearch() {
      this.options.page = 1;
      this.search = '';
      this.applySearch();
    },
    FilterByStatus(status) {
      if (typeof status != 'undefined' && status !== null)
        this.selectedStatus = '&status=' + status;
      else
        this.selectedStatus = '';

      this.getContracts();
    },
    rowClass(item) {
      return item.status == 'replaced' ? 'contract-replaced' : '';
    },
    downloadPdf(item) {
      if (!item.has_pdf) {
        return;
      }
      this.downloadingUuid = item.uuid;
      contractAPI
        .downloadContractPdf(item.uuid)
        .then((response) => {
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', 'contract-' + item.uuid + '.pdf');
          document.body.appendChild(link);
          link.click();
          // Contract pdfs are multi-MB; leaked object URLs pin them in memory
          // for the life of the SPA session.
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
          this.downloadingUuid = '';
        })
        .catch(() => {
          this.downloadingUuid = '';
          this.$snotify.error('The contract PDF could not be downloaded.');
        });
    },
    showReplaceDialog(item) {
      this.replaceItem = item;
      this.replaceFile = null;
      this.replaceConfirmed = false;
      this.messages = [];
      this.replaceDialog = true;
    },
    closeReplaceDialog() {
      this.replaceDialog = false;
      this.replaceItem = {};
      this.replaceFile = null;
      this.replaceConfirmed = false;
      this.messages = [];
    },
    processReplace() {
      if (!this.replaceFile || !this.replaceConfirmed) {
        return;
      }
      const isPdf =
        this.replaceFile.type == 'application/pdf' ||
        this.replaceFile.name.toLowerCase().endsWith('.pdf');
      if (!isPdf) {
        this.messages = { file: 'Only PDF files can be uploaded.' };
        return;
      }
      this.replaceLoading = true;
      contractAPI
        .replaceContract(this.replaceItem.uuid, this.replaceFile)
        .then(() => {
          this.replaceLoading = false;
          this.closeReplaceDialog();
          this.$snotify.success(this.successReplaceMessage);
          this.getContracts();
        })
        .catch((err) => {
          this.replaceLoading = false;
          this.messages = this.$gf.errorResponse(err);
          this.$snotify.error(this.errorMessage);
        });
    },
  },
};
</script>
<style>
.contracts-page .contract-replaced {
  opacity: 0.55;
}
</style>
